import { appliquerDelta } from '../delta-seance.js';
// ---------------------------------------------------------------------------
// Séance commune — côté client
//
// Ce qui est partagé : le fichier ouvert, la position, en lecture ou en pause.
// Rien d'autre. Les sous-titres, la piste audio, le volume, la langue et la
// qualité restent chez chacun — deux personnes peuvent regarder le même
// épisode, l'une en VF sans sous-titres, l'autre en VO avec des sous-titres
// jaunes, et c'est le but.
//
// La main : qui l'a pilote. Qui ne l'a pas peut demander une pause, et la
// demande part à ceux qui l'ont. On n'empêche donc jamais un geste sans
// dire pourquoi ni comment l'obtenir.
//
// Le VOTE, lui, ne s'adresse à personne : sauter l'intro ou passer à l'épisode
// suivant touche tout le monde en même temps, et s'exécute tout seul dès que le
// compte y est. Ce module ne décide rien de tout cela — le serveur compte, ce
// fichier ne fait que donner la voix, rapporter le compte au bouton, et poser
// le geste quand la décision revient.
//
// OÙ EST CHACUN : trois faits, pas un drapeau. Chaque participant arrive avec
//   `regarde`  — le lecteur est ouvert dans un navigateur ;
//   `activite` — l'activité Discord est ouverte, où l'on gère ;
//   `vocal`    — `true`, `false`, ou `null` quand le bot n'a pas su répondre.
// Les trois sont indépendants : on gère depuis Discord pendant que la lecture
// se joue sur la télé, on discute dans le vocal sans rien regarder. Le `null`
// de `vocal` n'est pas un `false` poli — `lecture().registreVocal.fiable` dit
// si la colonne veut dire quelque chose, et une colonne muette ne doit jamais
// se dessiner comme un salon vide.
//
// Personne n'entre dans cette liste par le vocal : le suivi commence quand on
// lance l'activité. Qui discute à côté sans l'avoir lancée n'est pas un
// participant, et ne pèse sur aucun seuil de vote.
// ---------------------------------------------------------------------------

// Trois seuils, et la raison de chacun :
const ECART_IGNORE = 0.25;   // en deçà, l'œil ne voit rien : corriger ferait plus de mal
const ECART_SAUT = 1.2;      // au-delà, on saute : rattraper prendrait trop longtemps
const RATTRAPAGE = 0.07;     // entre les deux, on joue 7 % plus vite ou moins vite
const CADENCE_ETAT = 1000;   // celui qui a la main pousse sa position à ce rythme
const CADENCE_HORLOGE = 20_000; // et l'on remesure l'écart entre les montres
const ATTENTE_RETOUR = 1500; // premier délai de reconnexion, doublé à chaque échec

// Le temps qu'une voix donnée d'ici mette à revenir comptée par le serveur.
//
// Entre le clic et l'écho, un état diffusé par le battement (3 s) ne contient
// pas encore le vote : oublier la voix sur cette seule absence ferait clignoter
// le bouton de « en attente » à « demander » puis retour. Deux secondes, c'est
// un aller-retour très au-delà de la latence mesurée, et bien en deçà de la
// minute au bout de laquelle un vote sans suite retombe.
const GRACE_VOIX = 2000;

// Le serveur efface au bout d'une minute une demande à laquelle personne n'a
// répondu, et il ne le dit à personne : l'état commun ne porte PAS les demandes
// (voir `vueDe` côté serveur), si bien que le battement qui suit la purge est
// tout ce que le client reçoit — un état où rien n'a changé. La même horloge
// doit donc tourner ici, des DEUX côtés du geste : le bouton de qui a demandé,
// et la carte de qui pouvait répondre.
//
// Trois secondes de plus que la minute du serveur, et c'est mesuré : il ne
// purge pas à la seconde près mais à chaque battement (CADENCE_ECHO = 3 s), si
// bien qu'une demande y vit entre 60 et 63 s. Expirer ici à 60 s pile ferait
// disparaître une carte à laquelle on pouvait encore répondre. Le décalage est
// dans le bon sens : une carte qui traîne trois secondes vaut mieux qu'un
// bouton qui n'a plus rien à commander.
const VIE_DEMANDE = 63_000;

// Les trois choses qu'on peut réclamer à qui pilote. La liste est celle du
// serveur, mot pour mot : il jette silencieusement tout le reste, et une
// interface qui proposerait une quatrième nature offrirait un bouton mort.
// Exportée pour que la console construise ses boutons sur CETTE liste, plutôt
// que d'en recopier une qui divergera.
export const NATURES_DEMANDE = new Set(['parole', 'pause', 'main']);

// Après un saut décidé en commun, le temps laissé au pilote pour que sa
// nouvelle position revienne. Voir `appliquerCommun` : elle vaut CADENCE_ETAT
// (1 s) plus un aller-retour.
const APRES_SAUT_COMMUN = 2000;

// Ce qu'un vote emporté vient de décider, en français dans la ligne d'infos.
const MOTS_VOTE = {
  intro: "passer l'intro",
  pause: 'la pause',
  suivant: "l'épisode suivant",
};

/**
 * Ce qu'une demande réclamait, pour l'annoncer à qui l'a formulée.
 *
 * La NATURE d'abord : « parole » et « main » n'ont pas d'`action`, et s'en
 * tenir à celle-ci faisait répondre « Demande de déplacement refusée » à
 * quelqu'un qui demandait le micro.
 */
function motDeLaDemande({ nature, action }) {
  if (nature === 'parole') return 'de parole';
  if (nature === 'main') return 'de la main';
  // La nature 'pause' couvre toute la famille des gestes de lecture ; c'est
  // `action` qui dit lequel exactement.
  if (action === 'play') return 'de lecture';
  if (action === 'seek') return 'de déplacement';
  return 'de pause';
}

// `sousTitresCourants` est facultatif : sans lui, la piste en service est lue
// sur la balise elle-même (voir `sousTitresEnCours`). C'est ce qui permet au
// second des deux tags de la console d'être rempli sans rien câbler ailleurs.
export function creerSeance({ base, $, lecteur, jeton, instance, moi, surChangement, surOuvrir, surParticipants, surRefus, surSourdine, langueCourante, sousTitresCourants, dOu }) {
  let ws = null;
  let etat = null;              // dernier état reçu du serveur
  let minuteurEtat = null;
  let minuteurHorloge = null;
  let attente = ATTENTE_RETOUR;
  let ferme = false;
  // Vrai entre un `onclose` annoncé et le `onopen` qui répare : voir les deux
  // plus bas. Sert uniquement à ne pas dire « reconnectée » à la toute
  // première connexion, qui n'a rien à réparer.
  let fuitDeconnexion = false;
  // Ce que `brancherVideo` a posé sur la balise, tant que `debrancherVideo` ne
  // l'a pas défait : la balise elle-même, et les trois écouteurs, gardés par
  // référence pour pouvoir les retirer un jour. Voir `debrancherVideo`.
  let ecouteursVideo = null;
  const demandes = new Map();
  // Les votes en cours, tels que le serveur les compte. On ne recompte rien
  // ici : deux comptes valent un désaccord, et c'est le sien qui déclenche.
  let votes = [];
  // Les actions pour lesquelles j'ai déjà donné ma voix, et QUAND — le serveur
  // ne nomme pas les votants, et sans cette trace le bouton ne saurait pas
  // distinguer « rejoindre » de « attendre ».
  const mesVoix = new Map();
  // Ce que J'AI réclamé et qui attend encore : nature -> { id, quand }. Le
  // serveur n'envoie les demandes qu'à ceux qui pilotent ; sans cette trace,
  // celui qui demande la parole n'a aucun moyen de savoir qu'il l'a déjà fait.
  const mesDemandes = new Map();
  let sautCommunJusqua = 0;

  // --- ce qui vient de nous, et qui ne doit pas repartir --------------------
  //
  // `<video>` ne dit pas QUI l'a mise en pause. Le même `pause` sort d'un clic,
  // d'un état qu'on vient d'appliquer, et d'une source qu'on remplace : sans
  // repère, la séance renvoie au serveur ce qu'elle vient elle-même de poser.
  //
  // Une ÉCHÉANCE plutôt qu'un drapeau à minuteur, et ce n'est pas cosmétique :
  // il y avait un `setTimeout` par geste, chacun rabaissant le drapeau sans
  // regarder les autres. Deux gestes qui se chevauchent — un saut commun qui
  // repose une source, le cas le plus courant — laissaient le PREMIER minuteur
  // armé découvrir la fenêtre du second avant sa fin, et le geste s'échappait.
  // Une échéance ne peut que reculer.
  //
  // 120 ms, parce que `play` et `pause` ne sont pas émis pendant l'appel mais
  // au tour de boucle suivant : rendre la main tout de suite ne couvrirait rien.
  const ECHO_GESTE = 120;
  let sansEchoJusqua = 0;

  /** Ce que la balise émet vient-il de nous plutôt que de la personne ? */
  const echoDeNous = () => Date.now() < sansEchoJusqua;

  /**
   * Pose un geste au nom du système, pas de la personne : ce qu'il fait émettre
   * à la balise ne repartira pas au serveur.
   *
   * Exposé au lecteur (voir `brancherSeance`) : lui aussi touche à la balise
   * sans que personne l'ait demandé — remplacer une source commence par la
   * vider, et vider une balise qui joue émet un `pause`.
   */
  function sansEcho(faire) {
    sansEchoJusqua = Math.max(sansEchoJusqua, Date.now() + ECHO_GESTE);
    return faire();
  }

  // Écart entre l'horloge du serveur et celle d'ici, et temps d'aller-retour.
  // Sans cette mesure, une montre mal réglée décale toute la séance : la
  // position commune est datée par le serveur, mais calculée ici.
  let decalageHorloge = 0;
  let latence = 0;
  const maintenantServeur = () => Date.now() + decalageHorloge;

  function mesurerHorloge() {
    if (ws?.readyState !== WebSocket.OPEN) return;
    const t0 = Date.now();
    enAttenteDePong = (ts) => {
      const t1 = Date.now();
      latence = t1 - t0;
      // L'aller et le retour sont supposés symétriques : l'heure du serveur au
      // moment où il a répondu correspond à la moitié du trajet.
      const estimation = ts + latence / 2;
      const ecart = estimation - t1;
      // Première mesure prise telle quelle, les suivantes lissées : une seule
      // réponse en retard ne doit pas déplacer la séance.
      decalageHorloge = decalageHorloge === 0 ? ecart : decalageHorloge * 0.7 + ecart * 0.3;
    };
    envoyer({ type: 'ping', t0 });
  }
  let enAttenteDePong = null;

  // L'identifiant vient du serveur, jamais d'ici : au demarrage de la seance
  // le profil n'a pas encore recu le sien, et il est ensuite remplace par un
  // autre objet — la seance garderait une reference perimee, se croirait
  // spectatrice, et refuserait toutes les commandes de celui qui pilote.
  let monId = null;
  const moiMeme = () => monId;
  // Tant qu'on ne sait pas qui l'on est, on ne bloque rien : une information
  // manquante ne doit jamais se traduire par un geste refuse.
  //
  // Le droit du participant fait foi sur la liste des mains : celle d'un
  // administrateur ne s'y trouve pas — elle ne lui a été donnée par personne,
  // il la porte. Se fier à `mains` seule le ferait passer pour spectateur ici
  // pendant que le serveur accepte tout ce qu'il envoie, et son bouton
  // proposerait de demander ce qu'il obtient déjà.
  const jAiLaMain = () => {
    if (!etat || !monId) return true;
    const p = (etat.participants ?? []).find((x) => x.id === monId);
    if (p) return !!p.main;
    return etat.hote === monId || (etat.mains ?? []).includes(monId);
  };
  const video = () => $('video');

  // `HTMLMediaElement.HAVE_METADATA` : durée et pistes connues, `currentTime`
  // a un sens. En dessous (HAVE_NOTHING), rien n'a jamais été chargé.
  const AU_MOINS_LES_METADONNEES = 1;

  /**
   * Est-ce que CE poste lit vraiment quelque chose ?
   *
   * Ce qu'on a vu : dans l'activité, `<video>` est dans la page sans
   * condition et le lecteur est bien instancié — mais aucune source n'y est
   * jamais posée, l'ouverture d'un épisode s'arrêtant avant la lecture quand
   * on est dans Discord. La balise répond donc position 0, en pause,
   * éternellement. Prendre son existence pour une lecture, c'est prendre ce
   * zéro pour une position : le serveur l'accepte, le rediffuse, et tous les
   * navigateurs repartent au générique — une fois par seconde. Un
   * administrateur ayant la main en permanence, ce n'est pas un cas rare mais
   * le chemin nominal : il tient la salle depuis l'activité pendant que la
   * lecture se joue ailleurs.
   *
   * Le critère est `readyState`, et pas un des trois autres candidats :
   * — « a-t-il une source ? » ne suffit pas : une adresse posée puis refusée
   *   (jeton périmé, fichier absent) laisse la balise vide, et l'on
   *   repousserait des zéros par un chemin de plus.
   * — « a-t-il une durée ? » arrive trop tard : sur un fichier remuxé, la
   *   durée est réclamée au serveur et met plusieurs secondes à revenir. Qui
   *   pilote serait muet pendant ce temps.
   * — « a-t-il déjà joué ? » refuse un zéro LÉGITIME : ouvrir un épisode et
   *   le laisser en pause sur le générique, c'est exactement position 0, et
   *   cela doit se voir chez les autres.
   *
   * `readyState` dit précisément « il y a un média là-dedans et je sais ce
   * qu'il contient », sans dépendre d'une comptabilité à nous. Il retombe à
   * zéro quand on quitte l'écran de lecture (la source y est retirée puis la
   * balise rechargée), ce qui fait taire du même coup un onglet reparti au
   * catalogue — qui poussait lui aussi des zéros s'il avait la main.
   */
  const jeLisVraiment = (v = video()) => !!v && v.readyState >= AU_MOINS_LES_METADONNEES;

  // ⚠️ L'ACTIVITÉ NE POUSSE JAMAIS DE POSITION. ELLE GÈRE, ELLE NE LIT PAS.
  //
  // Une même personne ouvre souvent les DEUX fenêtres : l'activité pour gérer,
  // l'onglet pour regarder. Toutes deux tiennent une socket de séance, et si
  // elle a la main, toutes deux poussent l'état COMMUN.
  //
  // Or la balise de l'activité n'a jamais quitté le début. Elle écrasait donc
  // chaque seconde la vraie position de l'onglet : Toji à onze secondes dans son
  // lecteur, et « 1 seconde » affichée partout, y compris chez les autres. En
  // mettant en pause, son onglet poussait une dernière fois et tout se recalait
  // — d'où l'impression que « la pause répare ».
  //
  // `jeLisVraiment` ne suffit pas à l'écarter : elle ne regarde que le
  // `readyState`, et l'activité charge assez de métadonnées pour le franchir.
  // La question n'est pas « cette balise est-elle prête ? » mais « cette
  // fenêtre est-elle celle qui REGARDE ? ». Le module le sait déjà : c'est
  // `dOu`, et c'est la même distinction qui sert au serveur à compter qui
  // regarde vraiment.
  //
  // La garde vaut pour l'état commun ET pour l'état personnel : le second
  // portait déjà une protection équivalente, celle-ci la rend explicite et
  // impossible à contourner par un `readyState` complaisant.
  const jeSuisLaFenetreQuiLit = () => (dOu ?? 'navigateur') !== 'activite';

  /**
   * La piste de texte réellement en service, en une étiquette courte.
   *
   * Le mode cherché est `hidden` autant que `showing` : le lecteur décode la
   * piste puis la peint lui-même, pour rendre contour, position et cartouche
   * réglables — il la laisse donc en `hidden`. Ne chercher que `showing` ne
   * trouverait jamais rien, et le tag resterait vide alors que les
   * sous-titres sont bien à l'écran.
   *
   * La langue prime sur le libellé : « fr » tient dans un tag, « Français
   * (autre source) » non — et le serveur coupe à 24 caractères.
   */
  function sousTitresEnCours() {
    if (sousTitresCourants) return sousTitresCourants();
    const pistes = video()?.textTracks;
    if (!pistes) return null;
    for (let i = 0; i < pistes.length; i += 1) {
      const p = pistes[i];
      if (!p || p.mode === 'disabled') continue;
      return (p.language || p.label || '').slice(0, 24) || null;
    }
    return null;
  }

  /**
   * Ce que le temps a emporté, des deux côtés du geste.
   *
   * Une demande sans réponse s'efface du serveur au bout d'une minute, et
   * SILENCIEUSEMENT : aucun verdict ne part, et l'état commun ne la portait
   * pas. Sans ce ménage, chacun des deux côtés reste devant un mensonge.
   *
   * Côté pilote, la carte survit à ce qu'elle commande : cliquer « Accepter »
   * ne fait alors RIEN — le serveur ne retrouve plus la demande et rend la main
   * sans un mot — pendant que celui qui a demandé la parole croit qu'on la lui
   * a refusée. Une carte qui ne commande plus rien doit disparaître.
   *
   * Côté demandeur, le bouton était bien rendu, mais sans un mot : voir son
   * « en attente » s'éteindre tout seul se lit comme un refus. On le dit.
   */
  function purgerLesDemandes() {
    const limite = Date.now() - VIE_DEMANDE;
    for (const [id, d] of demandes) if (d.recueA < limite) demandes.delete(id);
    for (const [nature, d] of mesDemandes) {
      if (d.quand >= limite) continue;
      mesDemandes.delete(nature);
      lecteur?.signaler?.(`Demande ${motDeLaDemande(d)} restée sans réponse.`);
    }
  }

  /** Les natures que j'ai réclamées et qui attendent encore une réponse. */
  const naturesEnAttente = () => [...mesDemandes.keys()];

  const envoyer = (message) => {
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message));
  };

  // --- synchronisation ------------------------------------------------------

  /** Position que la séance devrait avoir MAINTENANT, décalage réseau compris. */
  function positionAttendue(e) {
    if (!e) return null;
    // À l'heure du serveur, pas à celle d'ici — et la moitié du trajet retour
    // s'est déjà écoulée quand le message arrive.
    const ecoule = e.lecture ? (maintenantServeur() - e.majA) / 1000 : 0;
    return e.position + ecoule;
  }

  function appliquer(e) {
    const v = video();
    if (!v || !e || jAiLaMain()) return;

    const cible = positionAttendue(e);
    const ici = lecteur?.position?.() ?? v.currentTime;

    sansEcho(() => {
      // La pause et la reprise sont appliquées d'abord, sans condition : c'est
      // le geste que tout le monde doit voir au même instant.
      if (!e.lecture && !v.paused) v.pause();
      // ⚠️ SAUF HORS DE L'ÉCRAN (bug SEANCE-2610222121-pws) : chaque battement
      // de qui pilote relançait la balise mise de côté du spectateur, et
      // l'épisode jouait derrière la liste des séances. Le retour devant
      // l'écran la rattrape au battement suivant.
      if (e.lecture && v.paused && !lecteur?.estHorsEcran?.()) {
        v.play().catch((err) => {
          // ⚠️ UN REFUS DE LECTURE NE DOIT PLUS DISPARAÎTRE SANS UN MOT.
          //
          // `NotAllowedError` est le refus ATTENDU : la politique d'autoplay du
          // navigateur bloque toute lecture tant qu'aucun geste n'a eu lieu sur
          // la page — typique d'un onglet qu'on vient d'ouvrir sur une séance
          // déjà en cours. Ce n'est pas une panne, mais rester muet dessus en
          // est une pour la personne : elle voit l'image figée sur « lecture »
          // sans qu'aucun bouton ne le dise, sans savoir qu'il suffit de
          // toucher l'écran une fois. Tout autre refus (source coupée, média
          // corrompu) est une vraie panne, et se confondait jusqu'ici avec
          // le premier — les deux partaient dans le même `catch(() => {})`.
          if (err?.name === 'NotAllowedError') {
            lecteur?.demanderGeste?.();
          } else {
            console.warn('[séance] lecture refusée', err);
          }
        });
      }

      const ecart = cible === null ? 0 : cible - ici;
      if (Math.abs(ecart) > ECART_SAUT) {
        // Trop loin : rattraper prendrait une minute, on saute.
        v.playbackRate = 1;
        if (lecteur?.recaler) lecteur.recaler(cible);
        else v.currentTime = cible;
      } else if (e.lecture && Math.abs(ecart) > ECART_IGNORE) {
        // Assez près pour recoller sans saut : on joue un peu plus vite ou un
        // peu moins vite. À 7 %, l'oreille ne l'entend pas et l'écart d'une
        // seconde se résorbe en une quinzaine.
        v.playbackRate = ecart > 0 ? 1 + RATTRAPAGE : 1 - RATTRAPAGE;
      } else if (v.playbackRate !== 1) {
        v.playbackRate = 1;
      }
    });
  }

  /**
   * Ce que MOI je regarde : ma minute, ma langue, mon volume.
   *
   * L'etat commun ne dit que la position de qui pilote. La gestion, elle,
   * veut voir chaque page — et c'est la seule facon de reperer celui qui a
   * pris du retard ou qui ecoute en francais.
   */
  function pousserMonEtat() {
    const v = video();
    // Même garde que pour l'état commun, et pour une raison de plus : les deux
    // fenêtres d'une même personne — l'activité qui gère, l'onglet qui lit —
    // écrivent dans LE MÊME participant côté serveur. L'activité, qui ne lit
    // rien, écraserait chaque seconde la minute réelle de l'onglet, et la
    // console afficherait 0:00 pour quelqu'un qui en est à douze minutes.
    if (!jeSuisLaFenetreQuiLit() || !jeLisVraiment(v)) return;
    // Même raison que pour l'état commun : parti voir la liste n'est pas « a mis
    // en pause ». La console afficherait un pion arrêté là où la personne suit
    // toujours la séance.
    const misDeCoteMoi = lecteur?.estMisDeCote?.() === true;
    envoyer({
      type: 'moi',
      position: lecteur?.position?.() ?? v.currentTime,
      lecture: misDeCoteMoi ? !!etat?.lecture : !v.paused,
      langue: langueCourante?.() ?? null,
      // Deux étiquettes distinctes, et c'est voulu : on peut écouter en
      // japonais sans sous-titres comme en français avec. Sans ce champ, le
      // second des deux tags de la console restait vide pour tout le monde,
      // alors que le serveur le range déjà.
      sousTitres: sousTitresEnCours(),
      son: v.muted ? 0 : v.volume,
    });
  }

  function pousserEtat() {
    if (!jAiLaMain()) return;
    const v = video();
    // La balise, elle, est toujours là — c'est tout le piège. Voir
    // `jeLisVraiment` : une position de 0 alors que la séance est à douze
    // minutes n'est pas une position, c'est l'absence de lecteur.
    if (!jeSuisLaFenetreQuiLit() || !jeLisVraiment(v)) return;
    // ⚠️ QUITTER LE LECTEUR NE MET PAS LA SÉANCE EN PAUSE.
    //
    // Toji : « imaginons que ce soit un admin et qu'il ait envie d'aller faire
    // autre chose. Il veut fermer le lecteur, sauf qu'il ne veut pas que la
    // séance se coupe. »
    //
    // L'état commun porte `lecture: !video.paused`, et sortir de l'écran met la
    // balise en pause : celui qui tient la séance arrêtait donc l'épisode POUR
    // TOUT LE MONDE en allant simplement voir la liste des séances. Le
    // `sansEcho` du lecteur empêche l'événement de partir, mais pas ce
    // battement-ci, qui lit la balise une fois par seconde et n'a aucune raison
    // de se douter de quoi que ce soit.
    //
    // Une pause de navigation reste donc LOCALE : on continue d'annoncer ce que
    // la séance faisait, parce que c'est ce qu'elle fait toujours — personne
    // d'autre n'a rien demandé.
    const misDeCote = lecteur?.estMisDeCote?.() === true;
    envoyer({
      type: 'etat',
      position: lecteur?.position?.() ?? v.currentTime,
      lecture: misDeCote ? !!etat?.lecture : !v.paused,
      media: lecteur?.ref?.() ?? null,
      // La durée sert à la vue de gestion : sans elle, une position seule ne
      // dit pas où l'on en est.
      duree: lecteur?.totale?.() || v.duration || null,
    });
  }

  // --- le vote --------------------------------------------------------------
  //
  // Rien ne se décide ici. Le serveur compte les voix, connaît le seuil et
  // déclenche ; ce module donne la voix, rapporte le compte au bouton, et pose
  // le geste quand la décision revient.

  /** Ce dont le bouton de saut a besoin pour dire ce qu'il va faire. */
  const vueDesVotes = () => ({
    actif: !!etat,
    jAiLaMain: jAiLaMain(),
    // Le réglage voyage dans l'état : coupé, un pilote ouvre un vote comme
    // n'importe qui et y compte pour une voix.
    exemptionPilotes: etat?.reglages?.exemptionPilotes !== false,
    seuil: etat?.seuil ?? 0,
    votes,
    mesVoix: [...mesVoix.keys()],
  });
  const pousserAuLecteur = () => lecteur?.majSeance?.(vueDesVotes());

  /** Qui tranche seul n'ouvre pas de vote : sa voix n'a pas à être retenue. */
  const jAgisSeul = () => jAiLaMain() && etat?.reglages?.exemptionPilotes !== false;

  /**
   * Ma voix ne vaut que tant que le vote existe : expiré, emporté, ou balayé
   * par un nouvel épisode, il quitte l'état et le bouton doit redevenir
   * cliquable. La grâce couvre le trajet de la voix qui vient de partir.
   */
  function oublierLesVotesFinis() {
    const limite = Date.now() - GRACE_VOIX;
    for (const [action, quand] of mesVoix) {
      if (quand < limite && !votes.some((v) => v.action === action)) mesVoix.delete(action);
    }
  }

  /**
   * Donne sa voix : ouvre le vote s'il n'existe pas, s'y joint sinon.
   *
   * Un seul message pour les deux — c'est le serveur qui sait s'il y a déjà un
   * vote ouvert sur cette action, et lui seul. Choisir ici entre « ouvrir » et
   * « rejoindre » reviendrait à décider sur un état qu'on ne détient pas.
   */
  function voter(action) {
    envoyer({ type: 'vote', action });
    // Qui tranche seul n'ouvre pas de vote : retenir sa voix ferait clignoter
    // « en attente » le temps que l'action lui revienne.
    if (!jAgisSeul()) mesVoix.set(action, Date.now());
    // Le bouton passe en attente tout de suite : attendre l'écho, c'est laisser
    // croire que le clic n'a pas pris et en provoquer un second.
    pousserAuLecteur();
  }

  /**
   * Ce qu'un vote vient d'emporter — ou ce qu'un pilote a tranché seul.
   *
   * Le drapeau d'application distante est posé pendant le geste : sans lui, la
   * pause qu'on provoque ici repartirait au serveur comme une demande
   * personnelle, et chaque spectateur en enverrait une à qui pilote.
   */
  function appliquerCommun(action) {
    // Un poste qui ne lit rien n'a aucun geste à poser — et surtout aucune
    // position à pousser derrière. Sans ce garde-fou, chaque intro votée
    // renvoyait la salle au générique depuis l'activité d'un administrateur,
    // la ligne de poussée plus bas n'étant gardée que par la main.
    //
    // Le prix, mesuré : si la source est justement en train d'être rechargée
    // (un déplacement en mode remux, une seconde environ), le geste est manqué
    // ici. Recliquer le rattrape ; repousser un zéro, non.
    if (!jeLisVraiment()) return;
    const v = video();
    sansEcho(() => {
      // La pause est la seule que la séance sache poser elle-même. Où finit
      // l'intro et quel épisode vient ensuite ne se savent que dans le lecteur
      // — c'est ce qui garde ce module ignorant du catalogue.
      if (action === 'pause') { if (v && !v.paused) v.pause(); }
      else lecteur?.executerSaut?.(action);
    });
    if (action === 'pause') return;

    // Le point de lecture vient de bouger chez tout le monde, mais le serveur
    // porte encore celui d'avant : le premier état reçu renverrait chacun au
    // générique, puis la poussée du pilote l'en ressortirait une seconde plus
    // tard. Deux sauts pour un. On laisse donc passer le temps que la nouvelle
    // position revienne — qui pilote l'envoie tout de suite, les autres
    // patientent. La synchronisation reprend ensuite intacte, seuils compris.
    sautCommunJusqua = Date.now() + APRES_SAUT_COMMUN;
    // Après TOUT saut (intro, générique, épisode suivant), pas seulement l'intro.
    if (jAiLaMain()) pousserEtat();
  }

  // --- ce que fait la personne ---------------------------------------------

  function brancherVideo() {
    const v = video();
    // ⚠️ CE GARDE-FOU SUPPOSE QUE `fermer()` LE REMET TOUJOURS À ZÉRO.
    //
    // `dataset.seance === '1'` empêche de brancher deux fois la même balise —
    // sans lui, chaque état reçu du serveur (`brancherVideo` est appelée
    // depuis `type === 'seance'`, voir plus bas, donc à chaque battement)
    // reposerait un jeu complet d'écouteurs `play`/`pause`/`seeked` par-dessus
    // les précédents. Le marqueur ne devient donc un piège que si une séance
    // est fermée puis rouverte SANS recharger la page : `fermer()` doit alors
    // le retirer, avec les écouteurs qui vont avec — sinon la nouvelle
    // instance trouve le marqueur déjà posé, sort ici sans jamais s'accrocher
    // à la balise, et la personne perd tout geste local (elle SUBIT la
    // synchronisation sans pouvoir en émettre) sans qu'aucune erreur ne le
    // signale. Voir `debrancherVideo`, appelée par `fermer()` plus bas.
    if (!v || v.dataset.seance === '1') return;
    v.dataset.seance = '1';

    const geste = (action) => {
      if (echoDeNous()) return;

      // UNE BALISE QU'ON VIENT DE VIDER N'A ÉTÉ MISE EN PAUSE PAR PERSONNE.
      //
      // Le filet, en plus de `sansEcho`. Remplacer une source commence par
      // `pause()` puis `load()`, et l'algorithme de chargement remet
      // `readyState` à HAVE_NOTHING AVANT que le `pause` mis en file ne soit
      // distribué : quand ce gestionnaire s'exécute, la balise ne tient donc
      // plus rien. Un vrai clic, lui, arrive sur un média chargé.
      //
      // Ce qu'on y gagne : la déclaration explicite ne couvre que `poserSource`.
      // Or `main.js` coupe la source de son côté, dans `vue()`, dès qu'on quitte
      // l'écran de lecture — et OUVRIR un épisode y passe aussi, par l'écran de
      // chargement. C'est donc ce `pause`-là qui partait le premier, sans que
      // rien d'ici puisse le déclarer : sans ce garde-fou, chaque changement
      // d'épisode ouvrait un vote de pause chez chaque spectateur.
      //
      // Seulement la pause : un `play` sur une balise encore vide reste un vrai
      // geste — on appuie sur lecture pendant le chargement, et cette demande-là
      // doit partir.
      if (action === 'pause' && !jeLisVraiment(v)) return;

      if (jAiLaMain()) return pousserEtat();

      // LA PAUSE SE VOTE, elle ne se demande pas.
      //
      // Toji : « si une personne met pause, il y a un pop-up comme quoi tel
      // utilisateur demande de mettre pause. Moi je peux mettre pause parce que
      // je suis admin, sinon c'est le système du vote, de la même façon que
      // pour passer l'intro. »
      //
      // Elle partait en `commande`, c'est-à-dire en demande adressée à la seule
      // personne qui tient la séance : à elle d'accepter ou de refuser pour
      // tout le monde. Un vote, lui, demande à la salle — ce qui est le sens
      // d'arrêter une séance que tout le monde regarde. Le serveur savait déjà
      // le faire (`ACTIONS_VOTE` contient 'pause' depuis le début) ; rien ne
      // l'appelait.
      //
      // Qui pilote ne passe jamais ici : `jAiLaMain()` a déjà rendu plus haut.
      if (action === 'pause') {
        voter('pause');
        appliquerBientot();
        return;
      }

      // Le reste (play, saut) part en demande. On remet aussitôt la vidéo dans
      // l'état commun, sinon l'image partirait toute seule chez qui a cliqué.
      envoyer({ type: 'commande', action, position: lecteur?.position?.() ?? v.currentTime });
      surChangement?.({ ...lecture(), messagePassager: 'Demande envoyée à qui tient la séance.' });
      appliquerBientot();
    };

    // Gardés par référence (et non anonymes) : `debrancherVideo` en a besoin
    // pour les retirer un par un plutôt que de compter sur un `load()` ou un
    // remplacement de balise qui, ici, n'a jamais lieu.
    const surPlay = () => geste('play');
    const surPause = () => geste('pause');
    const surSeeked = () => { if (!echoDeNous() && jAiLaMain()) pousserEtat(); };

    v.addEventListener('play', surPlay);
    v.addEventListener('pause', surPause);
    v.addEventListener('seeked', surSeeked);

    ecouteursVideo = { v, surPlay, surPause, surSeeked };
  }

  /**
   * Défait ce que `brancherVideo` a posé : les trois écouteurs, et le
   * marqueur qui empêchait de les reposer par-dessus eux-mêmes.
   *
   * Appelée par `fermer()`. Sans elle, une séance refermée puis recréée sur
   * la même page retrouverait `dataset.seance === '1'` déjà présent sur la
   * balise et ne s'y raccrocherait jamais — voir le commentaire de
   * `brancherVideo`.
   */
  function debrancherVideo() {
    if (!ecouteursVideo) return;
    const { v, surPlay, surPause, surSeeked } = ecouteursVideo;
    v.removeEventListener('play', surPlay);
    v.removeEventListener('pause', surPause);
    v.removeEventListener('seeked', surSeeked);
    if (v.dataset.seance === '1') delete v.dataset.seance;
    ecouteursVideo = null;
  }

  // Un léger différé : l'événement `pause` arrive avant que la vidéo soit
  // réellement arrêtée, et appliquer trop tôt ne corrigerait rien.
  const appliquerBientot = () => setTimeout(() => appliquer(etat), 60);

  // --- connexion ------------------------------------------------------------

  function connecter() {
    if (ferme) return;
    const protocole = location.protocol === 'https:' ? 'wss:' : 'ws:';
    ws = new WebSocket(`${protocole}//${location.host}${base('/api/seance')}`);

    ws.onopen = () => {
      // ⚠️ LA RECONNEXION ÉTAIT MUETTE ELLE AUSSI.
      //
      // `fuitDeconnexion` n'est vrai qu'après un VRAI `onclose` (voir plus
      // bas) : la toute première connexion, à l'ouverture du lecteur, ne doit
      // rien annoncer — il n'y a rien eu à réparer. Ce n'est que la deuxième
      // fois, après une coupure déjà signalée, que « reconnecté » a un sens à
      // dire à la salle.
      if (fuitDeconnexion) {
        fuitDeconnexion = false;
        lecteur?.signaler?.('Séance reconnectée.');
      }
      attente = ATTENTE_RETOUR;
      envoyer({
        type: 'bonjour',
        jeton,
        instance,
        pseudo: moi?.pseudo ?? null,
        avatar: moi?.avatar ?? null,
        // Le rôle le plus haut sur le serveur, et sa couleur. Il vient de
        // `/api/moi`, qui interroge Discord — le client ne peut pas le déduire
        // seul, et sans lui la ligne du participant affichait son rôle DANS LA
        // SÉANCE (« Spectateur ») là où on attend « Fondateur ».
        rang: moi?.rang ?? null,
        rangCouleur: moi?.rangCouleur ?? null,
        // L'activite gere, le navigateur lit : la seance doit savoir lequel
        // des deux parle pour dire qui regarde vraiment.
        ou: dOu ?? 'navigateur',
        // Sans ce drapeau, le serveur n'envoie que des états entiers — ce qui
        // reste correct, mais coûte le carré de l'assistance en trafic.
        deltas: true,
      });
      clearInterval(minuteurEtat);
      minuteurEtat = setInterval(() => { pousserEtat(); pousserMonEtat(); }, CADENCE_ETAT);
      // L'écart d'horloge se mesure tout de suite, puis régulièrement : une
      // montre dérive, et le réseau change de temps de trajet.
      mesurerHorloge();
      clearInterval(minuteurHorloge);
      minuteurHorloge = setInterval(mesurerHorloge, CADENCE_HORLOGE);
    };

    ws.onmessage = (evt) => {
      let m;
      try {
        m = JSON.parse(evt.data);
      } catch (err) {
        // ⚠️ UN MESSAGE ILLISIBLE NE DOIT PLUS DISPARAÎTRE SANS TRACE.
        //
        // Ce `catch` avalait tout : une régression de format côté serveur (un
        // `JSON.stringify` oublié, une réponse HTML d'un proxy qui s'interpose)
        // rendrait la séance muette sans qu'aucun message d'erreur ne permette
        // de le retrouver — seul le symptôme (« plus rien ne bouge ») serait
        // visible, jamais sa cause. On journalise donc au lieu de se taire ;
        // on continue de ne RIEN appliquer, ce message-là ne pouvant être fait
        // sien.
        console.warn('[séance] message illisible', err, evt.data);
        return;
      }

      // ⚠️ UNE DIFFÉRENCE REDEVIENT UN ÉTAT ENTIER AVANT TOUTE AUTRE CHOSE.
      //
      // Tout ce qui suit — la vidéo, les votes, la main — est écrit pour un
      // état complet, et doit le rester : dupliquer ce traitement pour les
      // deltas ferait deux chemins qui divergeraient au premier changement
      // qu'on n'aurait corrigé que d'un côté. On reconstitue, puis on retombe
      // exactement dans la branche `seance` (l'état porte son propre `type`).
      //
      // Un refus n'est pas une panne : c'est la porte anti-dérive du serveur
      // qui joue. On redemande l'état entier plutôt que d'appliquer à
      // l'aveugle et d'afficher, en silence, une séance qui n'existe pas.
      if (m.type === 'seance-delta') {
        const entier = appliquerDelta(etat, m);
        if (!entier) { envoyer({ type: 'rafraichir' }); return; }
        m = entier;
      }

      if (m.type === 'toi') {
        monId = m.userId;
        // Savoir qui l'on est change ce que le bouton propose : jusque-là il
        // parlait sans savoir s'il agissait ou s'il demandait.
        pousserAuLecteur();
        surChangement?.(lecture());
        return;
      }

      if (m.type === 'seance') {
        const avait = jAiLaMain();
        etat = m;
        votes = m.votes ?? [];
        oublierLesVotesFinis();
        surParticipants?.(m.participants ?? []);
        brancherVideo();
        // Un saut décidé en commun vient d'avoir lieu ici : l'état qui arrive
        // porte encore le point d'avant, et le corriger le déferait.
        if (Date.now() >= sautCommunJusqua) appliquer(etat);
        pousserAuLecteur();
        surChangement?.(lecture());
        // Passer de spectateur à pilote change ce qu'on peut faire : autant le
        // dire, sinon on découvre la bascule en appuyant.
        if (!avait && jAiLaMain()) lecteur?.signaler?.('Tu tiens la séance.');
        return;
      }

      if (m.type === 'demande') {
        // Datée à la RÉCEPTION, et pas par le `quand` du serveur : les deux
        // horloges ne sont pas la même — c'est tout l'objet de `decalageHorloge`
        // — et une montre en avance ferait disparaître la carte à peine posée.
        demandes.set(m.demande.id, { ...m.demande, recueA: Date.now() });
        surChangement?.(lecture());
        return;
      }

      if (m.type === 'verdict') {
        demandes.delete(m.demande);
        // Le verdict revient aussi à qui a formulé la demande : sa trace part
        // avec, sinon le bouton resterait en attente une minute entière.
        for (const [nature, d] of mesDemandes) if (d.id === m.demande) mesDemandes.delete(nature);
        lecteur?.signaler?.(`Demande ${motDeLaDemande(m)} ${m.accepte ? 'acceptée' : 'refusée'}.`);
        surChangement?.(lecture());
        return;
      }

      // Un vote monte : le compte arrive avant l'état, et le bouton l'affiche
      // sans attendre le battement suivant.
      if (m.type === 'vote') {
        const i = votes.findIndex((v) => v.action === m.vote.action);
        if (i >= 0) votes[i] = m.vote; else votes = [...votes, m.vote];
        pousserAuLecteur();
        surChangement?.(lecture());
        return;
      }

      // Le seuil est atteint. L'action part seule, par 'action-commune' : rien
      // n'est à valider ici, sans quoi ce ne serait plus un vote.
      if (m.type === 'vote-adopte') {
        votes = votes.filter((v) => v.action !== m.vote.action);
        mesVoix.delete(m.vote.action);
        lecteur?.signaler?.(`C’est voté : ${MOTS_VOTE[m.vote.action] ?? 'action commune'}.`);
        pousserAuLecteur();
        surChangement?.(lecture());
        return;
      }

      if (m.type === 'action-commune') {
        appliquerCommun(m.action);
        return;
      }

      if (m.type === 'pong') {
        if (typeof m.ts === 'number' && enAttenteDePong) {
          enAttenteDePong(m.ts);
          enAttenteDePong = null;
        }
        return;
      }

      if (m.type === 'son') {
        // Couper le son chez tout le monde : c'est le seul geste ou l'on
        // touche a la page des autres sans leur demander, et il est reserve a
        // qui pilote.
        const v = video();
        if (v) v.muted = !!m.coupe;
        surChangement?.(lecture());
        return;
      }

      if (m.type === 'ouvrir') {
        // Les votes portaient sur l'épisode d'avant, le serveur les a vidés :
        // garder ma voix ici laisserait le bouton du nouvel épisode en attente
        // d'un vote qui n'existe plus.
        votes = [];
        mesVoix.clear();
        pousserAuLecteur();
        surOuvrir?.(m.cible);
        return;
      }

      // Une requête refusée : trop tôt après un non, ou requêtes coupées. Le
      // dire est indispensable — un bouton qui ne répond pas sans expliquer
      // pourquoi est exactement ce qui pousse à recliquer, et l'on fabriquerait
      // le spam qu'on prétend éteindre.
      if (m.type === 'requete-refusee') {
        surRefus?.(m);
        return;
      }

      // Ses propres requêtes viennent d'être coupées, ou rendues. C'est à
      // l'intéressé qu'on le dit, et à lui seul : le serveur ne l'envoie qu'à
      // sa socket.
      if (m.type === 'sourdine') {
        surSourdine?.(m);
        return;
      }

      if (m.type === 'demande-envoyee') {
        // L'écho porte la demande : sa nature dit au bouton qu'il attend, son
        // identifiant permettra de la retirer quand le verdict tombera. On
        // n'anticipe pas au moment du clic — une demande partie sur une socket
        // qui se ferme n'est jamais arrivée, et le bouton attendrait pour rien.
        // La nature ET l'action voyagent : sans l'action, annoncer l'expiration
        // dirait « demande de pause » à quelqu'un qui demandait à reprendre la
        // lecture ou à se déplacer — `motDeLaDemande` a besoin des deux.
        if (m.demande?.nature) {
          mesDemandes.set(m.demande.nature, {
            id: m.demande.id,
            nature: m.demande.nature,
            action: m.demande.action ?? null,
            quand: Date.now(),
          });
        }
        lecteur?.signaler?.('Demande envoyée.');
        surChangement?.(lecture());
      }
    };

    ws.onclose = () => {
      clearInterval(minuteurEtat);
      clearInterval(minuteurHorloge);
      // La séance perdue, la vitesse revient à la normale : sinon la lecture
      // resterait accélérée sans que rien ne la corrige.
      const v = video();
      if (v && v.playbackRate !== 1) v.playbackRate = 1;
      if (ferme) return;
      // ⚠️ UNE COUPURE NE DISAIT RIEN NON PLUS.
      //
      // Les minuteurs s'arrêtent, une reconnexion à délai croissant se
      // programme — et jusqu'ici, rien de tout ça n'était visible : la salle
      // voyait un état figé (plus de position qui avance, plus de participant
      // qui bouge) sans savoir si c'était un silence normal ou une connexion
      // perdue en train de revenir toute seule. `fuitDeconnexion` porte le
      // fait qu'on a bien annoncé une coupure, pour que `onopen` sache que la
      // prochaine connexion réussie mérite elle aussi un mot.
      fuitDeconnexion = true;
      lecteur?.signaler?.('Séance déconnectée — reconnexion en cours…');
      // Reconnexion à intervalle croissant : un service qui redémarre ne doit
      // pas se faire marteler par vingt clients.
      setTimeout(connecter, attente);
      attente = Math.min(attente * 2, 20_000);
    };

    ws.onerror = () => { try { ws.close(); } catch { /* déjà fermée */ } };
  }

  function lecture() {
    // Le temps efface des demandes que rien n'annonce : le ménage se fait ici,
    // à chaque lecture de l'état. C'est le seul endroit qui passe à coup sûr —
    // tout message du serveur y aboutit, donc au moins à chaque battement, et
    // c'est justement ce battement-là qui suit la purge du serveur.
    purgerLesDemandes();
    return {
      actif: !!etat,
      instance: etat?.instance ?? null,
      hote: etat?.hote ?? null,
      media: etat?.media ?? null,
      duree: etat?.duree ?? null,
      lecture: !!etat?.lecture,
      // ⚠️ L'ÉPISODE OUVERT, QUE CET ÉTAT NE PORTAIT PAS.
      //
      // Le serveur l'envoie depuis toujours (`ouvrir` dans la vue de séance),
      // mais il s'arrêtait ici : l'interface recevait `media`, `duree`,
      // `position`, les participants, les votes — tout SAUF ce qu'on regarde.
      //
      // C'est ce qui faisait afficher « aucun épisode » alors que la séance en
      // portait un, et c'est aussi pourquoi rouvrir le lecteur ne relançait
      // rien : la reprise lit ce champ, et le lisait toujours vide. Le défaut
      // n'était ni dans le serveur, qui savait, ni dans la reprise, qui était
      // juste — il était dans le tuyau entre les deux.
      //
      // Une information qui traverse le réseau et s'arrête à la dernière marche
      // est plus coûteuse à trouver qu'une information absente : tout le reste
      // arrive, donc on cherche partout ailleurs.
      ouvrir: etat?.ouvrir ?? null,
      // La position est rendue par une FONCTION : lue une fois, elle serait
      // figée jusqu'au message suivant et la barre avancerait par à-coups.
      position: () => positionAttendue(etat) ?? 0,
      jeSuisHote: etat?.hote === moiMeme(),
      jAiLaMain: jAiLaMain(),
      participants: etat?.participants ?? [],
      demandes: [...demandes.values()],
      // Les votes en cours, le seuil qu'il faut atteindre et combien de
      // personnes regardent : la console les montre sans les recalculer, sinon
      // elle finirait par afficher autre chose que ce que le serveur tranche.
      votes,
      seuil: etat?.seuil ?? 0,
      regardent: etat?.regardent ?? 0,
      // Les deux autres populations, à côté de celle qui compte pour les
      // votes. ⚠️ SEUL `regardent` borne un seuil : ces deux-là s'affichent,
      // elles ne décident rien. Les confondre ferait annoncer « 3 / 5 » à une
      // salle où deux personnes discutent sans rien regarder.
      dansLActivite: etat?.dansLActivite ?? 0,
      enVocal: etat?.enVocal ?? 0,
      // Ce que vaut la colonne « vocal » : `fiable: false` veut dire que le
      // bot ne répond pas, et surtout PAS que le salon est vide. Sans cette
      // distinction, une Gateway coupée se dessinerait comme une salle qui
      // s'est vidée d'un coup. `motif` dit pourquoi il ne sait pas, ce qui
      // envoie chercher la panne du bon côté.
      registreVocal: etat?.registreVocal
        ?? { fiable: false, motif: null, fraicheur: null, vuIlYA: null },
      reglages: etat?.reglages ?? null,
      mesVoix: [...mesVoix.keys()],
      // Ce que j'ai réclamé et qui attend : sans cette trace, le bouton
      // « Demander la parole » ne saurait pas qu'il l'a déjà été, et l'on
      // recliquerait jusqu'à ce que quelqu'un réponde.
      mesDemandes: naturesEnAttente(),
    };
  }

  // Le bouton de saut appartient au lecteur, la parole au serveur appartient
  // ici : on lui passe de quoi ouvrir un vote, il ne connaît toujours pas la
  // connexion.
  // `sansEcho` part avec : le lecteur remplace des sources, et une balise qu'on
  // vide en émet des `pause` que rien ne distingue d'un clic. Sans ce passage,
  // chaque changement de piste audio ou déplacement en mode remux ouvrait un
  // vote de pause chez les spectateurs, et poussait une pause à toute la salle
  // depuis qui pilote.
  lecteur?.brancherSeance?.({ voter, sansEcho });
  connecter();

  return {
    lecture,
    /** Coupe ou rend le son chez tout le monde. Réservé à qui pilote. */
    couperLeSon(coupe) {
      envoyer({ type: 'son', coupe });
    },

    /**
     * La règle du jeu : les seuils de vote, la carte du salon, l'exemption des
     * pilotes. Réservé à l'hôte et aux administrateurs — le serveur le vérifie.
     *
     * ⚠️ Cette porte MANQUAIT. `main.js` appelait `seance.reglages(...)` derrière
     * un `typeof … !== 'function'`, si bien que TOUT réglage de séance répondait
     * « Séance injoignable » sans qu'aucun message ne parte : le seuil de vote et
     * l'exemption des pilotes de l'onglet Admin étaient inopérants, et le garde-
     * fou faisait passer l'absence de méthode pour une connexion perdue. Le
     * serveur, lui, accepte ce message depuis le début.
     *
     * Rien n'est validé ici : le mode, la borne de la valeur et le droit de
     * changer la règle appartiennent au serveur, qui rejoue ensuite les votes en
     * cours au nouveau seuil. Filtrer une seconde fois ici donnerait deux
     * vérités à tenir d'accord.
     */
    reglages(charge) {
      envoyer({ type: 'reglages', ...charge });
    },

    /** Demande à tous d'ouvrir un épisode. Réservé à qui pilote. */
    ouvrir(cible) {
      envoyer({ type: 'ouvrir', ...cible });
    },

    /** Demande explicite, depuis le bandeau : même chemin que le geste. */
    demander(action) {
      envoyer({ type: 'commande', action, position: lecteur?.position?.() ?? 0 });
    },

    /**
     * Réclamer quelque chose à qui pilote : la parole, une pause, la main.
     *
     * À ne pas confondre avec `voter` : une demande s'adresse à quelqu'un, qui
     * accepte ou refuse. Un vote ne s'adresse à personne et part tout seul.
     *
     * C'est ici que tient toute la surface de l'interface : trois natures, pas
     * une de plus — `NATURES_DEMANDE`, exporté à côté, les énumère. Le retour
     * dit si la demande est réellement partie ; un bouton qui ne le sait pas
     * s'allume en attente d'un message que personne n'a reçu. Ce qui attend
     * une réponse se lit dans `lecture().mesDemandes`, en natures.
     */
    reclamer(nature) {
      if (!NATURES_DEMANDE.has(nature)) return false;
      // Demander la main quand on l'a déjà ouvrirait une pop-up sans objet
      // chez les autres pilotes — le serveur la jette, autant s'en abstenir.
      if (nature === 'main' && jAiLaMain()) return false;
      envoyer({ type: 'demander', nature });
      return ws?.readyState === WebSocket.OPEN;
    },

    /**
     * Ouvre un vote sur ce qui touche tout le monde, ou s'y joint.
     *
     * Le bouton de saut l'appelle de lui-même ; il est exposé ici pour la
     * console, où les mêmes gestes doivent rester à portée.
     */
    voter,
    repondre(id, accepte) {
      envoyer({ type: 'reponse', demande: id, accepte });
      demandes.delete(id);
      surChangement?.(lecture());
    },
    donnerLaMain(cible, donner) {
      envoyer({ type: 'main', cible, donner });
    },
    /**
     * Coupe les requêtes de quelqu'un, ou les lui rend.
     *
     * Rien n'est validé ici : le serveur vérifie qu'on a la main et que la
     * cible n'en a pas. Un client ne décide jamais de ses propres droits — il
     * ne fait que transmettre le geste.
     */
    sourdine(cible, rendre = false) {
      envoyer({ type: 'sourdine', userId: cible, rendre });
    },
    /** Pour l'affichage : à combien de millisecondes est le serveur. */
    latence: () => Math.round(latence),

    fermer() {
      ferme = true;
      clearInterval(minuteurEtat);
      clearInterval(minuteurHorloge);
      // ⚠️ SANS CECI, UNE SÉANCE ROUVERTE SUR LA MÊME PAGE NAÎT SOURDE.
      //
      // `brancherVideo` refuse de s'accrocher une seconde fois tant que
      // `dataset.seance` reste à '1' — voir son commentaire. Tant que rien
      // n'appelait `fermer()`, ce marqueur ne posait pas de problème en
      // pratique ; mais `fermer()` fait partie de l'objet public retourné
      // ici, pensé pour être rappelé un jour (fermer une séance sans recharger
      // la page), et le laisser sans nettoyage aurait déplacé le bug au jour
      // où quelqu'un l'appellerait enfin, sans lien évident avec ce fichier.
      debrancherVideo();
      try { ws?.close(); } catch { /* déjà fermée */ }
    },
  };
}
